import { Link2, Printer, TriangleAlert } from "lucide-react";
import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";
import {
	IMMEDIATE_DIALOG_NOTE,
	PLAN_DIALOG_NOTE,
	portKindLabel,
	removeReinstallNote,
} from "./printer-labels";
import type { PlanAction, PlanNames } from "./printer-plan";
import type { DeviceConnection, PrinterDevice, PrinterItem } from "./printers-api";

export type RemoveTarget =
	| { kind: "printer"; device: PrinterDevice; printer: PrinterItem }
	| {
			kind: "connection";
			device: PrinterDevice;
			connection: DeviceConnection & { userName: string };
	  };

export type DefaultCandidate = {
	/** Nazwa kolejki albo ścieżka połączenia — tak, jak Windows nazywa drukarkę domyślną. */
	value: string;
	label: string;
	detail: string;
	/** Działająca, prawdziwa drukarka — kandydatka do zaznaczenia na start. */
	preferred: boolean;
};

/**
 * Kandydatki na nową drukarkę domyślną: inne kolejki tego komputera (bez
 * przekierowanych z pulpitu zdalnego i bez wierszy spoza inwentarza), a przy
 * połączeniu także inne połączenia tego samego użytkownika Windows.
 */
export function defaultCandidates(target: RemoveTarget): DefaultCandidate[] {
	const { device } = target;
	const removed = target.kind === "printer" ? target.printer.name : target.connection.path;
	const queues = device.printers
		.filter((p) => p.name !== removed && p.portKind !== "redirected" && p.port !== "")
		.map((p) => ({
			value: p.name,
			label: p.name,
			detail: `${portKindLabel(p.portKind)}${p.offline ? " · wyłączona" : ""}`,
			preferred: !p.offline && p.portKind !== "virtual",
		}));
	if (target.kind === "printer") return queues;
	const connections = device.connections
		.filter((c) => c.path !== removed && c.userName === target.connection.userName)
		.map((c) => ({
			value: c.path,
			label: c.printerName ?? c.path,
			detail: c.hostDisplayName ? `sieciowa z ${c.hostDisplayName}` : "sieciowa",
			preferred: true,
		}));
	return [...queues, ...connections];
}

/**
 * „Usuń drukarkę” / „Usuń połączenie”: co zniknie i jak to odtworzyć, a gdy
 * pozycja jest domyślna — wybór nowej domyślnej (agent ustawia ją przed
 * usunięciem). Okno dopisuje pozycję do planu; usunięcie potwierdza się
 * jeszcze raz przed wykonaniem planu.
 */
export function RemoveDialog({
	target,
	onPlan,
	onClose,
	immediate = false,
}: {
	target: RemoveTarget | null;
	onPlan: (action: PlanAction, names: PlanNames) => void;
	onClose: () => void;
	/** Panel helpdesku: „Wykonaj” startuje operację od razu, bez planu. */
	immediate?: boolean;
}) {
	return (
		<Dialog open={target != null} onOpenChange={(next) => (next ? undefined : onClose())}>
			<DialogContent className="max-w-lg">
				{target ? (
					<RemoveDialogBody
						// Nowa pozycja = świeży formularz (wybór domyślnej, operacja).
						key={`${target.kind}:${target.device.deviceUuid}:${
							target.kind === "printer"
								? target.printer.name
								: `${target.connection.path}:${target.connection.userName}`
						}`}
						target={target}
						onPlan={onPlan}
						onClose={onClose}
						immediate={immediate}
					/>
				) : null}
			</DialogContent>
		</Dialog>
	);
}

function RemoveDialogBody({
	target,
	onPlan,
	onClose,
	immediate,
}: {
	target: RemoveTarget;
	onPlan: (action: PlanAction, names: PlanNames) => void;
	onClose: () => void;
	immediate: boolean;
}) {
	const groupId = useId();
	const { device } = target;
	const isDefault =
		target.kind === "printer" ? target.printer.isDefault : target.connection.isDefault;
	const candidates = isDefault ? defaultCandidates(target) : [];
	// `null` = „Windows wybierze sam”; na start pierwsza działająca prawdziwa drukarka.
	const [newDefault, setNewDefault] = useState<string | null>(
		() => (candidates.find((c) => c.preferred) ?? candidates[0])?.value ?? null,
	);
	const note = removeReinstallNote(target.kind === "printer" ? target.printer.portKind : null);
	// Udostępniona w Windows poza modułem: inne komputery (także bez agenta) mogą z niej drukować.
	const windowsShare =
		target.kind === "printer" && target.printer.shared && target.printer.managedShare == null
			? { name: target.printer.shareName }
			: null;

	const submit = () => {
		const chosen = isDefault ? newDefault : null;
		const chosenLabel = candidates.find((c) => c.value === chosen)?.label;
		if (target.kind === "printer") {
			onPlan(
				{
					kind: "remove_printer",
					input: {
						deviceUuid: device.deviceUuid,
						printerName: target.printer.name,
						newDefault: chosen,
					},
				},
				{ device: device.displayName, printer: target.printer.name, extra: chosenLabel },
			);
		} else {
			onPlan(
				{
					kind: "remove_connection",
					input: {
						deviceUuid: device.deviceUuid,
						path: target.connection.path,
						userName: target.connection.userName,
						newDefault: chosen,
					},
				},
				{
					device: device.displayName,
					printer: target.connection.printerName ?? target.connection.path,
					host: target.connection.hostDisplayName ?? undefined,
					extra: chosenLabel,
				},
			);
		}
		onClose();
	};

	// `slot` zamiast nazwy w id: nazwy kolejek są dowolne (także „auto”).
	const radio = (slot: string, value: string | null, label: string, detail: string | null) => {
		const inputId = `${groupId}-${slot}`;
		const checked = newDefault === value;
		return (
			<label
				key={inputId}
				htmlFor={inputId}
				className={cn(
					"flex min-h-9 cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-sm hover:bg-muted/50",
					checked && "border-primary bg-primary/5",
				)}
			>
				<input
					id={inputId}
					type="radio"
					name={groupId}
					value={value ?? ""}
					checked={checked}
					onChange={() => setNewDefault(value)}
					className="size-4 shrink-0 accent-primary"
				/>
				<span className="min-w-0 flex-1 truncate">{label}</span>
				{detail ? <span className="shrink-0 text-xs text-muted-foreground">{detail}</span> : null}
			</label>
		);
	};

	return (
		<>
			<DialogHeader>
				<DialogTitle>{target.kind === "printer" ? "Usuń drukarkę" : "Usuń połączenie"}</DialogTitle>
				<DialogDescription>
					{target.kind === "printer"
						? `Kolejka drukarki zniknie z komputera ${device.displayName}.`
						: `Drukarka sieciowa zniknie z konta Windows ${target.connection.userName} na komputerze ${device.displayName}.`}
				</DialogDescription>
			</DialogHeader>
			<DialogBody className="space-y-4">
				<div className="flex items-start gap-2 rounded-md border bg-muted/40 p-2 text-sm">
					{target.kind === "printer" ? (
						<Printer className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
					) : (
						<Link2 className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
					)}
					<div className="min-w-0">
						{target.kind === "printer" ? (
							<>
								<p className="font-medium break-words">{target.printer.name}</p>
								<p className="text-xs text-muted-foreground">
									{portKindLabel(target.printer.portKind)}
									{target.printer.port ? ` (${target.printer.port})` : ""} · komputer{" "}
									<span className="font-medium text-foreground">{device.displayName}</span>
									{target.printer.isDefault ? " · domyślna na tym komputerze" : ""}
								</p>
							</>
						) : (
							<>
								<p className="font-medium break-words">
									{target.connection.printerName ?? target.connection.path}
									{target.connection.hostDisplayName
										? ` z ${target.connection.hostDisplayName}`
										: ""}
								</p>
								<p className="font-mono text-xs break-all text-muted-foreground">
									{target.connection.path}
								</p>
								<p className="text-xs text-muted-foreground">
									Użytkownik {target.connection.userName} · komputer{" "}
									<span className="font-medium text-foreground">{device.displayName}</span>
									{target.connection.isDefault ? " · domyślna" : ""}
								</p>
							</>
						)}
					</div>
				</div>

				<p
					className={cn(
						"rounded-md border p-2 text-xs",
						note.manual ? statusClasses.warning : "border-border text-muted-foreground",
					)}
				>
					{note.text}
				</p>
				{windowsShare ? (
					<p
						className={cn(
							"flex items-start gap-2 rounded-md border p-2 text-xs",
							statusClasses.warning,
						)}
					>
						<TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
						<span>
							Drukarka jest udostępniona w Windows
							{windowsShare.name ? ` jako ${windowsShare.name}` : ""} — inne komputery mogą z niej
							drukować i po usunięciu stracą do niej dostęp.
						</span>
					</p>
				) : null}

				{isDefault ? (
					<fieldset className="space-y-1.5">
						<legend className="mb-1 text-sm font-medium">Nowa domyślna drukarka</legend>
						{candidates.map((c, i) => radio(`c${i}`, c.value, c.label, c.detail))}
						{radio("auto", null, "Windows wybierze sam", null)}
					</fieldset>
				) : null}
				<p className="text-xs text-muted-foreground">
					{immediate ? IMMEDIATE_DIALOG_NOTE : PLAN_DIALOG_NOTE}
				</p>
			</DialogBody>
			<DialogFooter>
				<Button variant="outline" size="sm" onClick={onClose}>
					Anuluj
				</Button>
				<Button size="sm" onClick={submit}>
					{immediate ? "Wykonaj" : "Dodaj do planu"}
				</Button>
			</DialogFooter>
		</>
	);
}
